import React, {
  MutableRefObject,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  PopoverContentAlign,
  PopoverContext,
  PopoverContextResponse,
} from "./popover-helper";
import classNames from "classnames";

const useClickOutside = <T extends HTMLElement | HTMLDivElement>(
  ref: React.RefObject<T>,
  callback: (event: MouseEvent) => void
): void => {
  const handleClick = (e: MouseEvent): void => {
    if (ref.current && !ref.current.contains((e.target as Node) || null)) {
      callback(e);
    }
  };
  useEffect(() => {
    document.addEventListener("click", handleClick);

    return (): void => {
      document.removeEventListener("click", handleClick);
    };
  }, [ref as MutableRefObject<T>, callback]);
};

export default useClickOutside;

interface PopoverProps {
  /**
   * Alignment
   */
  align?: PopoverContentAlign;
  /**
   * Extend classes
   */
  extendClasses?: string;
  /**
   * Content
   */
  children?: React.ReactNode;
}

const Root: React.FC<PopoverProps> = ({
  align = "left-bottom",
  extendClasses,
  children,
}) => {
  const [opened, setOpened] = useState(false);
  const context: PopoverContextResponse = useMemo(
    () => ({
      opened,
      handleToggle: () => setOpened(!opened),
    }),
    [opened]
  );
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpened(false));
  return (
    <PopoverContext.Provider value={context}>
      <div
        className={classNames({
          tfcPopover: true,
          [`tfcPopover-${align}`]: true,
          [extendClasses as string]: !!extendClasses,
        })}
        ref={ref}
      >
        {children}
      </div>
    </PopoverContext.Provider>
  );
};

interface PopoverTriggerProps {
  /**
   * Extend classname
   */
  extendClasses?: string;
  /**
   * Content
   */
  children?: React.ReactNode;
}

const Trigger: React.FC<PopoverTriggerProps> = ({
  extendClasses,
  children,
}) => {
  const { opened, handleToggle } = useContext(PopoverContext);

  return (
    <div
      className={classNames({
        ["tfcPopover-trigger"]: true,
        ["tfcPopover-trigger-active"]: opened,
        [extendClasses as string]: !!extendClasses,
        [`${extendClasses}-active`]: opened,
      })}
      onClick={handleToggle}
    >
      {children}
    </div>
  );
};

const Content: React.FC<PopoverTriggerProps> = ({
  extendClasses,
  children,
}) => {
  const { opened, handleToggle } = useContext(PopoverContext);
  return (
    <div
      className={classNames({
        ["tfcPopover-content"]: true,
        ["tfcPopover-content-active"]: opened,
        [extendClasses as string]: !!extendClasses,
        [`${extendClasses}-active`]: opened,
      })}
    >
      <div onClick={handleToggle}>{children}</div>
    </div>
  );
};

export const Popover = Object.assign(Root, { Trigger, Content });
